import { useMemo, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { ApiError } from '@api/index';
import { clearBasketStorage } from '@utils/basket';
import { getBasketFromCache } from '@hooks/cache';
import { callService } from '@api/caller';

type BasketState = 'stale' | 'error' | 'ok';
type ResolveCallback = (v: BasketState) => void;
type RejectCallback = (v: unknown) => void;

const ensureCleanBasket = () => {
  let resolve: ResolveCallback | undefined, reject: RejectCallback | undefined;
  const promise = new Promise<BasketState>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { cleanBasketSignal: promise, resolve, reject };
};

const useUnlockBasket = ({
  onSuccess,
  basketId,
}: {
  onSuccess?: () => Promise<unknown> | void;
  basketId: string;
}) => {
  const { cleanBasketSignal, resolve } = useMemo(() => ensureCleanBasket(), []);
  const [errorType, setErrorType] = useState('');

  const prebookUnlockMutation = useMutation(
    ['unlockPrebook'],
    async () => {
      try {
        await callService('BasketsService', 'unlock', {
          basketId,
          body: {
            basketId,
          },
        });
      } catch (e) {
        const { detail } = (e as ApiError).body?.errors?.[0] ?? {};
        setErrorType(detail);

        if (/archieved/gi.test(detail)) {
          clearBasketStorage(true);
          resolve?.('stale');
          return {};
        } else {
          resolve?.('error');
          throw e;
        }
      }
      resolve?.('ok');
      return {};
    },
    {
      onSuccess: onSuccess,
    }
  );

  return {
    unlockBasket: () => prebookUnlockMutation.mutate(),
    unlockBasketAsync: () => {
      const basket = getBasketFromCache();
      const shouldSendUnlockRequest = basket?.id && basket.isLocked;
      if (!shouldSendUnlockRequest) {
        resolve?.('ok');
        return;
      }
      return prebookUnlockMutation.mutateAsync();
    },
    isError: prebookUnlockMutation.isError,
    errorType: errorType,
    cleanBasketSignal,
    reset: prebookUnlockMutation.reset,
  };
};

export default useUnlockBasket;
